import { getRouteStart } from "../pages/dashboard/route-planner/helpers/route-start";
import { useEffect, useRef, useState, useMemo } from "react";
import { DateTime } from "luxon";
import { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";
import { RouteStatus } from "../pages/dashboard/route-planner/helpers/route-status.enum";
import {
  isSkippedStop,
  stopServiceMinutes,
} from "../pages/dashboard/route-planner/helpers/stop-service-minutes";
import useRoutePlannerStore from "../state/route-planner-store";

// Stable empty Set to avoid creating new references on every render
const EMPTY_SET = new Set<string>();

// Statuses that indicate the driver is done with the location
// partiallyCompleted and cantPlow should be treated the same as completed
// because the driver has finished with that location and moved on
const DONE_STATUSES = ["completed", "partiallyCompleted", "cantPlow"] as const;

const isDoneStatus = (status: string | undefined): boolean => {
  return DONE_STATUSES.includes(status as (typeof DONE_STATUSES)[number]);
};

const isAdminDone = (location: AdminGetRoutesLocationsResDto): boolean =>
  isDoneStatus(location.driverStatus) &&
  location.driverStatusLastUpdatedBy === "admin";

// Travel time (minutes) between two consecutive admin-completed stops, derived
// from their completion timestamps. Admin completions involve no actual travel
// — a bulk update stamps all stops at (nearly) the same instant — so the
// planned travelTimeBetweenLocations would wrongly spread the run forward past
// the current-time indicator.
const getAdminCompletionGapMinutes = (
  location: AdminGetRoutesLocationsResDto,
  nextLocation: AdminGetRoutesLocationsResDto,
): number => {
  if (
    !location.driverStatusLastUpdatedAt ||
    !nextLocation.driverStatusLastUpdatedAt
  ) {
    return 0;
  }

  const doneAt = DateTime.fromISO(location.driverStatusLastUpdatedAt);
  const nextDoneAt = DateTime.fromISO(nextLocation.driverStatusLastUpdatedAt);

  if (!doneAt.isValid || !nextDoneAt.isValid) {
    return 0;
  }

  return Math.max(0, nextDoneAt.diff(doneAt, "minutes").minutes);
};

export interface TimeAdjustments {
  adjustedServiceTime?: number;
  adjustedTravelTime?: number;
  adjustedEstimatedTimeMinutes?: number; // For timeline recalculation
}

export interface UseRealtimeTimeAdjustmentsResult {
  adjustments: Record<string, TimeAdjustments>;
}

interface UseRealtimeTimeAdjustmentsProps {
  locations: AdminGetRoutesLocationsResDto[];
  routeStartTime: string;
  routeDate: string;
  currentRouteStatus?: RouteStatus;
  // Optional: locations that received backend updates (to reset adjustments)
  backendUpdatedLocationIds?: Set<string>;
}

// Helper to create a status map for comparison
const createStatusMap = (
  locations: AdminGetRoutesLocationsResDto[],
): Map<string, string | undefined> => {
  const map = new Map<string, string | undefined>();
  locations.forEach((loc) => {
    map.set(loc.locationId, loc.driverStatus);
  });
  return map;
};

const createTimingSignature = (loc: AdminGetRoutesLocationsResDto) =>
  JSON.stringify({
    estimatedTimeMinutes: loc.travelDetails?.estimatedTimeMinutes,
    serviceTimeMin: loc.travelDetails?.serviceTimeMin,
    travelTimeBetweenLocations: loc.travelDetails?.travelTimeBetweenLocations,
    serviceTime: loc.serviceTime,
  });

const createTimingMap = (
  locations: AdminGetRoutesLocationsResDto[],
): Map<string, string> => {
  const map = new Map<string, string>();
  locations.forEach((loc) => {
    map.set(loc.locationId, createTimingSignature(loc));
  });
  return map;
};

/**
 * Hook for calculating real-time adjustments to service time and travel time
 * when actual durations exceed estimates. Updates every minute but calculates
 * with second-level precision for accurate decimal minute values.
 */
export const useRealtimeTimeAdjustments = ({
  locations,
  routeStartTime,
  routeDate,
  currentRouteStatus,
  backendUpdatedLocationIds,
}: UseRealtimeTimeAdjustmentsProps): UseRealtimeTimeAdjustmentsResult => {
  // Use stable empty Set to avoid creating new references on every render
  const safeBackendUpdatedLocationIds = backendUpdatedLocationIds ?? EMPTY_SET;
  const [adjustments, setAdjustments] = useState<
    Record<string, TimeAdjustments>
  >({});
  const previousStatusMapRef = useRef<Map<string, string | undefined>>(
    new Map(),
  );
  const previousTimingMapRef = useRef<Map<string, string>>(new Map());

  // Use centralized synced time from store - this ensures all components
  // update at exactly the same moment when the minute changes
  const syncedCurrentTime = useRoutePlannerStore(
    (state) => state.syncedCurrentTime,
  );

  // Only run adjustments in Live or Planned phases
  // The temporary "Unassigned" route has no start time ("") and nothing to
  // adjust; parsing it gives NaN hours, which makes Luxon throw and crash the page.
  const hasValidStartTime = DateTime.fromFormat(
    routeStartTime ?? "",
    "HH:mm",
  ).isValid;
  const shouldRunAdjustments =
    hasValidStartTime &&
    (currentRouteStatus === RouteStatus.Live ||
      currentRouteStatus === RouteStatus.Planned);

  // Detect which locations had status changes (backend updates)
  // BUT: Don't mark locations as changed when they transition TO a done status
  // because we want to preserve our real-time adjustments until backend recalculates (EST_TIME_CALCULATED event)
  const statusChangedLocationIds = useMemo(() => {
    const changed = new Set<string>();
    const previousStatusMap = previousStatusMapRef.current;

    locations.forEach((loc) => {
      const prevStatus = previousStatusMap.get(loc.locationId);
      const currentStatus = loc.driverStatus;

      // If status changed (including from undefined to a value), check if we should reset
      if (prevStatus !== currentStatus) {
        // Don't reset adjustments when transitioning TO a done status
        // This preserves our real-time calculated service time until backend recalculates
        // The backend will send ROUTE_EST_TIME_CALCULATED event when it has proper values
        const isTransitioningToDone =
          prevStatus === "inProgress" && isDoneStatus(currentStatus);

        if (!isTransitioningToDone) {
          changed.add(loc.locationId);
        }
      }
    });

    return changed;
  }, [locations]);

  const timingChangedLocationIds = useMemo(() => {
    const changed = new Set<string>();
    const previousTimingMap = previousTimingMapRef.current;

    locations.forEach((loc) => {
      const previousTiming = previousTimingMap.get(loc.locationId);
      if (previousTiming === undefined) {
        return;
      }

      const currentTiming = createTimingSignature(loc);

      if (previousTiming !== currentTiming) {
        changed.add(loc.locationId);
      }
    });

    return changed;
  }, [locations]);

  // Combine all locations that need reset (status changes + explicit backend updates)
  const locationsToReset = useMemo(() => {
    const combined = new Set<string>();
    statusChangedLocationIds.forEach((id) => combined.add(id));
    timingChangedLocationIds.forEach((id) => combined.add(id));
    safeBackendUpdatedLocationIds.forEach((id) => combined.add(id));
    return combined;
  }, [
    statusChangedLocationIds,
    timingChangedLocationIds,
    safeBackendUpdatedLocationIds,
  ]);

  // Update adjustments when syncedCurrentTime changes (centralized timing)
  // This ensures service/travel time updates happen at the exact same moment as
  // the current time indicator moves
  useEffect(() => {
    if (!shouldRunAdjustments) {
      setAdjustments({});
      previousStatusMapRef.current = new Map();
      previousTimingMapRef.current = new Map();
      return;
    }

    // Helper function to calculate adjustments
    const calculateAdjustments = (
      resetLocationIds: Set<string>,
      previousAdjustments: Record<string, TimeAdjustments>,
    ) => {
      // Use the synced current time from the store for all calculations
      const now = syncedCurrentTime;
      const routeStartDateTime = getRouteStart(routeStartTime, routeDate);
      if (!routeStartDateTime.isValid) return {};

      const newAdjustments: Record<string, TimeAdjustments> = {};
      // Start with the delay from starting point to first address
      let cumulativeDelay = 0;

      // The server lists a route's stops in the order its cumulative service
      // and travel times follow, finished stops first when the driver went out
      // of order. Skipped stops aren't driven and get no adjustment.
      const serviceMinutes = stopServiceMinutes(locations);
      const drivenLocations = locations.filter(
        (location) => !isSkippedStop(location),
      );
      // No stop has started: delay departure by every elapsed minute, preserving
      // the remaining travel. Subtract any delay already incorporated by the API.
      if (
        currentRouteStatus === RouteStatus.Live &&
        drivenLocations.length &&
        drivenLocations.every(
          (location) =>
            !isDoneStatus(location.driverStatus) &&
            location.driverStatus !== "inProgress",
        )
      ) {
        const first = drivenLocations[0].travelDetails;
        if (first?.estimatedTimeMinutes !== undefined) {
          const reflectedDelay = Math.max(
            0,
            first.estimatedTimeMinutes -
              (first.travelTimeBetweenLocations ?? 0),
          );
          cumulativeDelay = Math.max(
            0,
            Math.floor(now.diff(routeStartDateTime, "minutes").minutes) -
              reflectedDelay,
          );
        }
      }
      drivenLocations.forEach((location, index) => {
        const locationId = location.locationId;
        const travelDetails = location.travelDetails;
        const driverStatus = location.driverStatus;
        const serviceTime =
          serviceMinutes[locationId] ?? location.serviceTime ?? 0;

        // For locations that need reset, do NOT use any previous adjustments
        // Start fresh with backend values only
        const shouldResetThisLocation = resetLocationIds.has(locationId);
        const prevAdjustmentsForLocation = shouldResetThisLocation
          ? undefined
          : previousAdjustments[locationId];

        const locationAdjustments: TimeAdjustments = {};

        // Service Time Adjustment: For addresses with driverStatus === "inProgress"
        if (
          driverStatus === "inProgress" &&
          travelDetails?.estimatedTimeMinutes !== undefined
        ) {
          // For inProgress addresses, the backend has already updated estimatedTimeMinutes
          // to reflect the actual arrival time. Do NOT add cumulativeDelay here as that
          // would double-count the delays that are already reflected in the backend's ETA.
          const actualArrivalTime = routeStartDateTime.plus({
            minutes: travelDetails.estimatedTimeMinutes,
          });

          // Calculate elapsed time since ACTUAL arrival in seconds for precision
          // Then convert to decimal minutes: currentTime - actualArrivalTime
          const elapsedSeconds = Math.max(
            0,
            now.diff(actualArrivalTime, "seconds").seconds,
          );
          const elapsedMinutes = elapsedSeconds / 60; // Decimal minutes for precision

          // If current time > actual arrival + service time, update service time
          const expectedCompletionTime = actualArrivalTime.plus({
            minutes: serviceTime,
          });

          if (now > expectedCompletionTime) {
            // Service time should be the difference: current time - actual arrival time
            // Use decimal minutes for precision (e.g., 5.5 minutes instead of 5)
            const adjustedServiceTime = elapsedMinutes;
            locationAdjustments.adjustedServiceTime = adjustedServiceTime;
            const serviceTimeDelay = adjustedServiceTime - serviceTime;
            cumulativeDelay += serviceTimeDelay;

            // Store it immediately in newAdjustments so it's available for travel time calc
            newAdjustments[locationId] = {
              ...newAdjustments[locationId],
              adjustedServiceTime,
            };
          }
        }

        // Travel Time Adjustment: For done addresses (completed, partiallyCompleted, cantPlow)
        // Stop updating when next address status becomes "inProgress" or done (travel is complete)
        if (isDoneStatus(driverStatus) && index < drivenLocations.length - 1) {
          const nextLocation = drivenLocations[index + 1];

          // If next address is inProgress or done, travel is complete - use backend data
          // Don't recalculate travel time for past events
          if (
            nextLocation.driverStatus === "inProgress" ||
            isDoneStatus(nextLocation.driverStatus)
          ) {
            // Consecutive admin-completed stops (batch completion) have no
            // actual travel between them — override the planned travel time
            // with the completion-timestamp gap (~0 for a bulk update) so the
            // whole run collapses behind the current-time indicator instead
            // of spreading planned travel times into the future. Renders as a
            // deck-of-cards stack via computeCompletedStackShifts.
            if (isAdminDone(location) && isAdminDone(nextLocation)) {
              locationAdjustments.adjustedTravelTime =
                getAdminCompletionGapMinutes(location, nextLocation);
            }
            // But still preserve the adjustedServiceTime if it exists (unless this location is being reset)
            const prevAdjustedServiceTime =
              prevAdjustmentsForLocation?.adjustedServiceTime;
            if (prevAdjustedServiceTime !== undefined) {
              locationAdjustments.adjustedServiceTime = prevAdjustedServiceTime;
            }
            // Still need to add to newAdjustments if we have adjustments
            if (
              locationAdjustments.adjustedServiceTime !== undefined ||
              locationAdjustments.adjustedTravelTime !== undefined ||
              locationAdjustments.adjustedEstimatedTimeMinutes !== undefined
            ) {
              newAdjustments[locationId] = locationAdjustments;
            }
            return;
          }

          const nextLocationTravelDetails = nextLocation.travelDetails;
          const estimatedTravelTime =
            nextLocationTravelDetails?.travelTimeBetweenLocations;

          if (estimatedTravelTime !== undefined && travelDetails) {
            // For completed addresses, use the backend's estimatedTimeMinutes
            const thisAddressETA = travelDetails.estimatedTimeMinutes;
            const nextAddressETA =
              nextLocationTravelDetails?.estimatedTimeMinutes;

            // Calculate completion time using the most reliable method:
            // If next address has an ETA, derive completion time from it:
            // completionTime = nextAddressETA - travelTimeBetweenLocations
            // This is more reliable than using serviceTimeMin differences which can be inconsistent
            let completionTime: DateTime;

            if (
              nextAddressETA !== undefined &&
              estimatedTravelTime !== undefined
            ) {
              // Derive completion time from next address's ETA
              // Next ETA = this completion + travel time
              // So: this completion = next ETA - travel time
              completionTime = routeStartDateTime.plus({
                minutes: nextAddressETA - estimatedTravelTime,
              });
            } else {
              // Fallback: use this address ETA + service time
              if (thisAddressETA === undefined) {
                return;
              }

              const currentAdjustedServiceTime =
                newAdjustments[locationId]?.adjustedServiceTime;
              const previousAdjustedServiceTime =
                prevAdjustmentsForLocation?.adjustedServiceTime;

              const effectiveServiceTime =
                currentAdjustedServiceTime ??
                previousAdjustedServiceTime ??
                serviceTime;

              completionTime = routeStartDateTime
                .plus({
                  minutes: thisAddressETA,
                })
                .plus({
                  minutes: effectiveServiceTime,
                });
            }

            // Calculate elapsed travel time in seconds for precision: currentTime - completionTime
            // This is the ACTUAL travel time that has elapsed, independent of any delays
            // We should NOT add service time delays here - only the actual elapsed time
            const elapsedTravelSeconds = Math.max(
              0,
              now.diff(completionTime, "seconds").seconds,
            );
            const elapsedTravelMinutes = elapsedTravelSeconds / 60; // Decimal minutes for precision

            // If elapsed travel time > estimated travelTimeBetweenLocations, use elapsed time
            // The adjustedTravelTime should be the actual elapsed time, NOT including any service time delays
            if (elapsedTravelMinutes > estimatedTravelTime) {
              locationAdjustments.adjustedTravelTime = elapsedTravelMinutes;
              // Only add the travel time delay (not service time delay) to cumulativeDelay
              // The travel time delay is: actual travel time - estimated travel time
              const travelTimeDelay =
                elapsedTravelMinutes - estimatedTravelTime;
              cumulativeDelay += travelTimeDelay;
            }

            // Preserve adjustedServiceTime if it exists from previous calculations (unless being reset)
            const preservedServiceTime =
              newAdjustments[locationId]?.adjustedServiceTime ??
              prevAdjustmentsForLocation?.adjustedServiceTime;
            if (preservedServiceTime !== undefined) {
              locationAdjustments.adjustedServiceTime = preservedServiceTime;
            }
          }
        }

        // Calculate adjusted estimatedTimeMinutes for future addresses only
        // Do NOT update ETAs for done or inProgress addresses (they already happened)
        // Only update ETAs for addresses that haven't been reached yet
        if (
          cumulativeDelay > 0 &&
          travelDetails?.estimatedTimeMinutes !== undefined &&
          !isDoneStatus(driverStatus) &&
          driverStatus !== "inProgress"
        ) {
          locationAdjustments.adjustedEstimatedTimeMinutes =
            travelDetails.estimatedTimeMinutes + cumulativeDelay;
        }

        if (
          locationAdjustments.adjustedServiceTime !== undefined ||
          locationAdjustments.adjustedTravelTime !== undefined ||
          locationAdjustments.adjustedEstimatedTimeMinutes !== undefined
        ) {
          // Only merge with previous if this location is NOT being reset
          if (shouldResetThisLocation) {
            // Fresh start - only use newly calculated adjustments
            newAdjustments[locationId] = locationAdjustments;
          } else {
            // MERGE with previous location adjustments to preserve data we don't recalculate
            newAdjustments[locationId] = {
              ...prevAdjustmentsForLocation,
              ...locationAdjustments,
            };
          }
        } else if (prevAdjustmentsForLocation && !shouldResetThisLocation) {
          // Preserve previous adjustments if we didn't calculate new ones (and not being reset)
          newAdjustments[locationId] = prevAdjustmentsForLocation;
        }
        // If shouldResetThisLocation and no new adjustments, don't add anything (clean reset)
      });

      return newAdjustments;
    };

    // Calculate adjustments with current state
    // The useEffect runs when syncedCurrentTime changes, triggering recalculation
    setAdjustments((prevAdjustments) => {
      // Clear adjustments for locations being reset BEFORE calculation
      const currentAdjustments = { ...prevAdjustments };
      locationsToReset.forEach((locationId) => {
        delete currentAdjustments[locationId];
      });

      return calculateAdjustments(locationsToReset, currentAdjustments);
    });

    // Update the status map AFTER calculation so next render can detect new changes
    previousStatusMapRef.current = createStatusMap(locations);
    previousTimingMapRef.current = createTimingMap(locations);
  }, [
    shouldRunAdjustments,
    currentRouteStatus,
    routeStartTime,
    routeDate,
    locations,
    locationsToReset,
    syncedCurrentTime, // Recalculate when centralized time changes
  ]);

  return { adjustments };
};
